'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

import { benefitsId, howItWorksId, servicesId } from '@/utils/sectionIds';

import type { FooterConfigType } from '@/components/organisms/Footer/Footer.types';

import { SOCIALS_LINKS } from './common';

import { env } from '@/env.mjs';

const staticUrl = env.NEXT_PUBLIC_STATIC_WWW_URL_PL;

export const useFooter = (): FooterConfigType => {
    const [promoCodesModalVisible, setPromoCodesModalVisible] = useState(false);

    const location = usePathname();

    useEffect(() => {
        setPromoCodesModalVisible(false);
    }, [location]);

    return {
        links: [
            {
                name: 'PayPo',
                logoIsVisible: true,
                menu: {
                    list: [
                        {
                            name: 'O nas',
                            href: '/p/o-nas',
                            'data-test-id': 'footer-about-us-link',
                        },
                        {
                            name: 'Bezpieczeństwo',
                            href: '/p/bezpieczenstwo',
                            'data-test-id': 'footer-security-link',
                        },
                        {
                            name: 'Członkostwo w ZPF',
                            href: '/p/zpf',
                            'data-test-id': 'footer-zpf-link',
                        },
                        {
                            name: 'Składanie ofert na audyt',
                            href: `${staticUrl}/documents/tenders/zaproszenie-do-zlozenia-oferty-audytu.pdf`,
                            target: '_blank',
                            'data-test-id': 'footer-tenders-audit-link',
                        },
                        {
                            name: 'Dostępność w PayPo',
                            href: '/p/dostepnosc',
                            'data-test-id': 'footer-accessibility-link',
                        },
                        {
                            name: 'Blog',
                            href: 'https://paypo.pl/blog/',
                            target: '_blank',
                            'data-test-id': 'footer-blog-link',
                        },
                        {
                            name: 'Kariera',
                            href: '/p/kariera',
                            'data-test-id': 'footer-career-link',
                        },
                    ],
                },
                socialLinks: SOCIALS_LINKS,
            },
            {
                name: 'Dla klientów',
                menu: {
                    lead: {
                        href: '/',
                    },
                    list: [
                        {
                            name: 'Jak to działa?',
                            relatedSectionId: howItWorksId,
                            href: `/#${howItWorksId}`,
                            'data-test-id': 'footer-nav-how-it-works-link',
                        },
                        {
                            name: 'Usługi PayPo',
                            relatedSectionId: servicesId,
                            href: `/#${servicesId}`,
                            'data-test-id': 'footer-nav-services-ref',
                        },
                        {
                            name: 'Korzyści',
                            relatedSectionId: benefitsId,
                            href: `/#${benefitsId}`,
                            'data-test-id': 'footer-nav-benefits-link',
                        },
                        {
                            name: 'Dostępne sklepy',
                            href: '/p/dostepnesklepy',
                            'data-test-id': 'footer-available-shops-page-link',
                        },
                        {
                            name: 'Płać z aplikacją',
                            href: '/p/plac-z-aplikacja',
                            'data-test-id':
                                'footer-mobile-pay-with-app-page-link',
                        },
                        {
                            name: 'Aplikacja mobilna',
                            href: '/p/aplikacja-mobilna',
                            'data-test-id': 'footer-mobile-app-page-link',
                        },
                        {
                            name: 'Kody promocyjne',
                            onClick: () => setPromoCodesModalVisible(true),
                            'data-test-id': 'footer-promo-codes-link',
                        },
                        {
                            name: 'Kontakt',
                            href: '/p/kontakt',
                            'data-test-id': 'footer-contact-page-link',
                        },
                    ],
                },
            },
            {
                name: 'Dla sklepów',
                menu: {
                    list: [
                        {
                            name: 'Dlaczego warto?',
                            href: `/biznes#section-why-worth`,
                            'data-test-id': 'footer-partners-why-worth-ref',
                        },
                        {
                            name: 'Dostępne sklepy',
                            href: '/p/dostepnesklepy',
                            'data-test-id':
                                'footer-partners-available-shops-page-link',
                        },
                        {
                            name: 'Integracja',
                            href: `/biznes/integracja`,
                            'data-test-id': 'footer-partners-integration-link',
                        },
                        {
                            name: 'Nawiąż współpracę',
                            href: `/biznes#section-collaboration`,
                            'data-test-id': 'footer-partners-contact-page-link',
                        },
                    ],
                },
            },
            {
                name: 'Informacje prawne',
                menu: {
                    list: [
                        {
                            name: 'Regulaminy',
                            href: '/p/regulaminy',
                            'data-test-id': 'footer-regulations-page-link',
                        },
                        {
                            name: 'Archiwum Regulaminów',
                            href: '/p/archiwum-regulaminow',
                            'data-test-id':
                                'footer-regulations-archive-page-link',
                        },
                        {
                            name: 'Polityka prywatności i cookies',
                            href: '/p/polityka-prywatnosci-i-cookies',
                            'data-test-id':
                                'footer-regulations-privacy-policy-page-link',
                        },
                        {
                            name: 'Informacje o przetwarzaniu danych osobowych',
                            href: '/p/informacja-o-przetwarzaniu-danych-osobowych',
                            'data-test-id': 'footer-gdpr-page-link',
                        },
                        {
                            name: 'Archiwum Obowiązków Informacyjnych',
                            href: '/p/archiwum-obowiazkow-informacyjnych',
                            'data-test-id':
                                'footer-information-obligation-page-link',
                        },
                        {
                            name: 'Struktura organizacyjna',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_PL}/documents/Struktura_PayPo.pdf`,
                            target: '_blank',
                            'data-test-id':
                                'footer-struktura-paypo-document-link',
                        },
                        {
                            name: 'Ład korporacyjny',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_PL}/documents/Lad_korporacyjny_PayPo.pdf`,
                            target: '_blank',
                            'data-test-id':
                                'footer-lad-korporacyjny-document-link',
                        },
                        {
                            name: 'Polityka informacyjna',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_PL}/documents/Polityka_informacyjna.pdf`,
                            target: '_blank',
                            'data-test-id':
                                'footer-polityka-informacyjna-document-link',
                        },
                        {
                            name: 'Centrum zarządzania cookies',
                            onClick: () => {
                                //@ts-ignore
                                Cookiebot.renew();
                            },
                            'data-test-id':
                                'footer-cookies-management-center-link',
                        },
                    ],
                },
            },
        ],
        iconsListData: {
            title: 'Pobierz aplikację',
            links: [
                {
                    image: {
                        src: '/images/icons/app-store-button.svg',
                        alt: 'App Store',
                        className: 'max-w-[108px]',
                    },
                    href: 'https://apps.apple.com/pl/app/paypo/id1581411073?l=pl',
                },
                {
                    image: {
                        src: '/images/icons/google-play-button.svg',
                        alt: 'Google Play',
                        className: 'max-w-[108px]',
                    },
                    href: 'https://play.google.com/store/apps/details?id=com.paypo.mobile',
                },
            ],
        },
        footerInfo: [
            'PayPo Sp. z o.o. z siedzibą w Warszawie, ul. Domaniewska 39 (02-672 Warszawa), wpisana do rejestru przedsiębiorców prowadzonego przez Sąd Rejonowy dla m.st. Warszawy w Warszawie XIII Wydział Gospodarczy Krajowego Rejestru Sądowego pod nr KRS 0000575158, o kapitale zakładowym w wysokości 1 174 750,00 złotych, NIP: 521-37-05-997, Regon: 362485126. Spółka wpisana do rejestru instytucji pożyczkowych pod numerem RIP000135 oraz rejestru krajowych instytucji płatniczych pod numerem IP59/2024 prowadzonych przez Komisję Nadzoru Finansowego.',
        ],
        promoCodesModal: {
            isVisible: promoCodesModalVisible,
            config: {
                description:
                    'Pula poniższych kodów promocyjnych została wyczerpana. Komunikat dotyczy tylko akcji ograniczonych ilościowo.',
                noDataMessage:
                    'Obecnie kody z wszystkich aktywnych kampanii są dostępne.',
                title: 'Kody promocyjne',
                footerProps: {
                    config: [
                        {
                            children: 'Regulamin kodów promocyjnych',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_PL}/documents/Regulamin-kodow-promocyjnych.pdf`,
                            target: '_blank',
                            variant: 'ghost',
                            size: 's',
                        },
                        {
                            children: 'Zamknij',
                            onClick: () => setPromoCodesModalVisible(false),
                            variant: 'outline',
                            size: 's',
                        },
                    ],
                    reverseOnMobile: true,
                },
                onOpenChange: () => setPromoCodesModalVisible(false),
            },
        },
    };
};
